/**
 * Video Editor — Surface 7: done (Phase B3).
 *
 * Download the finished render + ship-to actions. Download is the dominant
 * action; the filename follows the Content Review naming convention (Q15).
 * The MCL action records the hand-off; the full submission bridge is a
 * tracked follow-up.
 */
import React, { useState, useEffect } from 'react';
import type { VideoProject } from '../types';
import type { VideoEditorApi } from '../videoEditorApi';
import { SurfaceHeader, formatDuration, formatBytes } from './parts';

interface Props {
  project: VideoProject;
  api: VideoEditorApi;
  onRefresh: () => Promise<any>;
  onReRender: () => void;
}

const SHIP_LABELS: Record<string, string> = {
  downloaded: 'Downloaded',
  sent_to_mcl: 'Sent to MCL for brand review',
  archived: 'Archived',
};

const DoneSurface: React.FC<Props> = ({ project, api, onRefresh, onReRender }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const render = project.renderOutput || {};
  const hasRender = !!render.vmPath;

  // Load the finished render for inline playback. /download is auth-gated, so
  // it is fetched as a blob and played from an object URL (revoked on unmount
  // / re-render). Re-runs when a new render lands (renderedAt changes).
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoErr, setVideoErr] = useState<string | null>(null);
  useEffect(() => {
    if (!hasRender) { setVideoUrl(null); return; }
    let revoke: string | null = null;
    let cancelled = false;
    setVideoErr(null);
    api.loadRenderUrl(project._id)
      .then(url => {
        if (cancelled) { URL.revokeObjectURL(url); return; }
        revoke = url;
        setVideoUrl(url);
      })
      .catch((e: any) => { if (!cancelled) setVideoErr(e.message || 'Could not load the video'); });
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project._id, hasRender, render.renderedAt]);

  async function download() {
    try {
      setBusy('download'); setErr(null); setNote(null);
      const r = await api.ship(project._id, 'download');
      await api.downloadRender(project._id, r.download?.fileName || 'video.mp4');
      await onRefresh();
    } catch (e: any) {
      setErr(e.response?.data?.error || e.message || 'Download failed');
    } finally { setBusy(null); }
  }

  async function ship(action: 'mcl' | 'archive') {
    try {
      setBusy(action); setErr(null); setNote(null);
      const r = await api.ship(project._id, action);
      if (action === 'mcl' && r.mcl) {
        setNote(`Hand-off recorded. Content Review filename: ${r.mcl.conformantFileName}`);
      }
      await onRefresh();
    } catch (e: any) {
      setErr(e.response?.data?.error || e.message || `Could not ${action}`);
    } finally { setBusy(null); }
  }

  return (
    <div>
      <SurfaceHeader
        eyebrow="Step 3 of 3"
        title="Your video is ready"
        subtitle="Download it, send it into MCL brand review, or archive the project."
      />
      {err && <div className="ve-banner ve-banner--error">{err}</div>}
      {note && <div className="ve-banner">{note}</div>}
      {project.shipState && (
        <div className="ve-banner">Latest action: {SHIP_LABELS[project.shipState] || project.shipState}</div>
      )}

      <div className="ve-panel ve-done">
        <div className="ve-done__stage">
          {videoUrl && !videoErr
            ? <video
                className="ve-done__video"
                src={videoUrl}
                controls
                playsInline
                onError={() => setVideoErr('this browser could not play the file')}
                onLoadedMetadata={(e) => {
                  // A black frame with audible sound means the browser decoded
                  // the audio but not the video stream — the render used a
                  // codec/pixel-format that is not browser-safe. The download
                  // still opens fine in a desktop player.
                  if (e.currentTarget.videoWidth === 0) {
                    setVideoErr("this browser can't decode the video stream");
                  }
                }}
              />
            : <span className="ve-done__play">{videoErr ? '⚠' : '▶'}</span>}
        </div>
        <div className="ve-done__info">
          <h3>{project.title}</h3>
          <p className="ve-muted">
            {render.durationSec ? `${formatDuration(render.durationSec)} · ` : ''}
            {formatBytes(render.sizeBytes) || ''}
            {project.format?.aspect ? ` · ${project.format.aspect}` : ''}
          </p>
          <button
            className="ve-btn ve-btn--primary ve-btn--lg"
            disabled={!hasRender || !!busy}
            onClick={download}
          >{busy === 'download' ? 'Preparing…' : '↓ Download .mp4'}</button>
          {videoErr && (
            <p className="ve-muted">Preview unavailable ({videoErr}) — the download still works.</p>
          )}
          {hasRender && !videoUrl && !videoErr && (
            <p className="ve-muted">Loading preview…</p>
          )}
        </div>
      </div>

      <div className="ve-shipgrid">
        <div className="ve-panel ve-ship">
          <h3>Send to MCL for brand review</h3>
          <p className="ve-muted">
            Routes the render into the existing Content Review pipeline. Does not
            skip the MCL regulatory gate.
          </p>
          <button className="ve-btn" disabled={!hasRender || !!busy} onClick={() => ship('mcl')}>
            {busy === 'mcl' ? 'Sending…' : 'Send to MCL'}
          </button>
        </div>
        <div className="ve-panel ve-ship">
          <h3>Archive project</h3>
          <p className="ve-muted">
            Closes the project. The VM working copy is cleaned after a grace window.
          </p>
          <button className="ve-btn" disabled={!hasRender || !!busy} onClick={() => ship('archive')}>
            {busy === 'archive' ? 'Archiving…' : 'Archive'}
          </button>
        </div>
      </div>

      <div className="ve-panel">
        <h3>Render details</h3>
        <div className="ve-statgrid">
          <div className="ve-stat"><div className="ve-stat__v">{formatDuration(render.durationSec) || '—'}</div><div className="ve-stat__l">Duration</div></div>
          <div className="ve-stat"><div className="ve-stat__v">{formatBytes(render.sizeBytes) || '—'}</div><div className="ve-stat__l">File size</div></div>
          <div className="ve-stat"><div className="ve-stat__v">{project.format?.aspect || '—'}</div><div className="ve-stat__l">Aspect</div></div>
          <div className="ve-stat"><div className="ve-stat__v">{(project.brand || '—').toString()}</div><div className="ve-stat__l">Brand</div></div>
        </div>
        <p className="ve-muted" style={{ marginTop: 12 }}>
          Not happy with it? <button className="ve-link" onClick={onReRender}>Refine and re-render</button>.
        </p>
      </div>

      <div className="ve-panel">
        <h3>Coming soon</h3>
        <div className="ve-chips">
          {['Post to Instagram', 'Post to TikTok', 'Add to Monday', 'Send to client portal'].map(c => (
            <button key={c} className="ve-chip" disabled>{c}</button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DoneSurface;
